// SeoPageGenerator.js - Builds one guide page (/guides/<country>/<slug>/<slug>-seo.html).
// Same design as the city pages: styles in public/city-page.css (prefix lx-, guide parts lx-g-),
// behaviour in public/city-page.js, header and footer loaded by public/seo-scripts.js.
import { SITE_BASE, getCountryName } from './guidesSite';
import { GOOGLE_TAG, stores, player, sentences, shorten, tidyTitle } from './pageParts';

const monthYear = () => new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

const coordsOf = (point) => {
  const c = (point && point.coordinates) || {};
  const lat = Number(c.latitude !== undefined ? c.latitude : c.lat);
  const lng = Number(c.longitude !== undefined ? c.longitude : c.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0) ? { lat, lng } : null;
};

/** "Castelo de São Jorge: Stairs that count as cardio" → place and tagline */
const splitTitle = (title) => {
  const i = title.indexOf(':');
  return i > 0 ? [title.slice(0, i).trim(), title.slice(i + 1).trim()] : [title, ''];
};

/**
 * Google Maps link: the walking route through every stop when Maps can take them all (up to 10 points),
 * otherwise the starting point.
 */
const mapsLink = (stops) => {
  const pts = stops.map(s => s.coords).filter(Boolean);
  if (!pts.length || !stops[0].coords) return null;
  const ll = (p) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;
  if (pts.length >= 2 && pts.length === stops.length && pts.length <= 10) {
    const mid = pts.slice(1, -1).map(ll).join('|');
    return { label: 'Open the route in Google Maps', url: `https://www.google.com/maps/dir/?api=1&origin=${ll(pts[0])}&destination=${ll(pts[pts.length - 1])}${mid ? `&waypoints=${encodeURIComponent(mid)}` : ''}&travelmode=walking` };
  }
  return { label: 'See the starting point in Google Maps', url: `https://www.google.com/maps/search/?api=1&query=${ll(stops[0].coords)}` };
};

const paragraphs = (text) => String(text || '').split(/\r?\n/).map(p => p.trim()).filter(Boolean);

/**
 * Generates a complete guide page.
 * @param {Object} guide - the guide data (metadata and points)
 * @param {Object} options
 *   canonicalUrl - final public URL of the page
 *   audioSrc     - first-stop recording that really plays ('./audio-1.mp3'); null for none.
 *                  Left out: the stop's own audioUrl.
 *   country, countrySlug - country name and its slug in /guides/<country>/
 *   cityPage     - { city, url } when the city has its own page
 *   related      - other guides of the same city: [{ name, url, thumbnail, stops }]
 * @returns {string} - Complete HTML string
 */
export function generateSeoPage(guide, options = {}) {
  const metadata = guide.metadata || {};
  const points = guide.points || [];
  const N = points.length;
  const name = tidyTitle(metadata.name || 'Guide');
  const cityPage = options.cityPage || null;
  const city = (cityPage && cityPage.city) || String(metadata.city || '').trim();
  const country = options.country || (metadata.country ? getCountryName(metadata.country) : '');
  const countrySlug = options.countrySlug || (country ? createSlug(country) : '');
  const url = options.canonicalUrl || `${SITE_BASE}/guides/${createSlug(metadata.name || guide.id || 'guide')}/`;
  const lang = metadata.language || 'en';
  const image = metadata.thumbnailUrl || points[0]?.photoUrl || '';
  const audioSrc = 'audioSrc' in options ? options.audioSrc : points[0]?.audioUrl;
  const related = (options.related || []).slice(0, 6);
  const updated = monthYear();

  const stops = points.map((p, i) => {
    const [place, tagline] = splitTitle(tidyTitle(p.name || `Stop ${i + 1}`));
    return { n: i + 1, place, tagline, coords: coordsOf(p), teaser: sentences(p.description || p.inDepthDescription, 210) };
  });
  const first = points[0];
  const firstStop = stops[0];
  const maps = mapsLink(stops);
  const intro = sentences(metadata.description, 280);
  const about = paragraphs(metadata.description);

  // ── FAQ ──
  const startPlace = firstStop ? firstStop.place : '';
  const listed = stops.slice(0, 3).map(s => s.place);
  const stopList = N > 3 ? `${listed.join(', ')} and ${N - 3} more` : listed.length > 1 ? `${listed.slice(0, -1).join(', ')} and ${listed[listed.length - 1]}` : listed[0];
  const faqItems = [
    ...(startPlace ? [[`Where does the ${name} audio guide start?`, `At ${startPlace}${city ? `, in ${city}` : ''}. From there the app takes you from stop to stop with GPS.`]] : []),
    ...(N > 1 ? [['How many stops are there?', `${N} stops: ${stopList}. The first one is free to read on this page.`]] : []),
    ['Does it work offline?', 'Yes. Download the guide and its audio over Wi-Fi, then use it outdoors with GPS without spending mobile data.'],
    ['Is it free?', "The GuideSofia app is free to download, and it's free to start your first tour."],
    ['Which language is it in?', `This page is in ${getLanguageName(lang)}. In the app, the guide is narrated in 30+ languages.`],
    ['Can I ask the guide questions?', 'Yes. Open the chat in the app and ask about what you are looking at, local history, food or nearby places.'],
  ];
  const faq = faqItems.map(([q, a], i) => `<details class="lx-faq-item"${i === 0 ? ' open' : ''}><summary>${escapeHtml(q)}</summary><p>${escapeHtml(a)}</p></details>`).join('');

  // ── structured data ──
  const crumbs = [
    ['Audio guides', `${SITE_BASE}/guides/`],
    ...(country ? [[country, `${SITE_BASE}/guides/${countrySlug}/`]] : []),
    ...(cityPage ? [[city, SITE_BASE + cityPage.url]] : []),
    [name, url],
  ];
  const ld = [
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: crumbs.map(([n, item], i) => ({ '@type': 'ListItem', position: i + 1, name: n, item })) },
    { '@context': 'https://schema.org', '@type': 'TouristTrip', name, url, inLanguage: lang,
      ...(metadata.description ? { description: metadata.description } : {}),
      ...(image ? { image } : {}),
      touristType: 'Cultural tourism',
      dateModified: new Date().toISOString().split('T')[0],
      provider: { '@type': 'Organization', name: 'GuideSofia', url: 'https://www.guidesofia.com/' },
      itinerary: { '@type': 'ItemList', numberOfItems: N, itemListElement: points.map((p, i) => ({
        '@type': 'ListItem', position: i + 1, item: {
          '@type': 'TouristAttraction', name: stops[i].place,
          ...(stops[i].teaser ? { description: stops[i].teaser } : {}),
          ...(stops[i].coords ? { geo: { '@type': 'GeoCoordinates', latitude: stops[i].coords.lat, longitude: stops[i].coords.lng } } : {}),
        } })) } },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faqItems.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })) },
  ];
  const ldHtml = ld.map(x => `  <script type="application/ld+json">${JSON.stringify(x).replace(/</g, '\\u003c')}</script>`).join('\n');

  const title = `${name}: ${city ? `${city} ` : ''}Audio Guide${N > 1 ? ` (${N} Stops)` : ''} | GuideSofia`;
  const description = shorten(N > 1
    ? `Self-guided audio tour${city ? ` in ${city}` : ''}: ${N} stops, from ${stops[0].place} to ${stops[N - 1].place}. Read the first stop free, then hear them all in the app.`
    : `${intro || `Self-guided audio guide${city ? ` in ${city}` : ''}.`} Read it free, then hear it in the GuideSofia app.`, 160);

  // ── sections ──
  const heroPlayer = audioSrc && first
    ? player({ url: audioSrc, label: 'Hear the first stop', small: firstStop.place, aria: `Play stop 1: ${firstStop.place}` }, 'lx-play-hero')
    : '';
  const crumbHtml = [
    '<a href="/guides/">Audio guides</a>',
    ...(country ? [`<a href="/guides/${escapeHtml(countrySlug)}/">${escapeHtml(country)}</a>`] : []),
    ...(cityPage ? [`<a href="${escapeHtml(cityPage.url)}">${escapeHtml(city)}</a>`] : []),
    `<b aria-current="page">${escapeHtml(name)}</b>`,
  ].join('<span>/</span>');

  // a long first stop folds after three paragraphs; the rest stays in the page for search engines
  const firstParas = first ? paragraphs(first.inDepthDescription || first.description).map(p => `<p>${escapeHtml(p)}</p>`) : [];
  const firstBody = firstParas.length > 4
    ? `${firstParas.slice(0, 3).join('')}<details class="lx-g-more"><summary>Keep reading</summary>${firstParas.slice(3).join('')}</details>`
    : firstParas.join('');
  const firstSection = first ? `
  <section class="lx-sec" id="first-stop">
    <div class="lx-g-first${first.photoUrl ? ' has-img' : ''}">
      <div class="lx-g-text">
        <p class="lx-kicker">Stop 1 of ${N} · free to read</p>
        <h2>${escapeHtml(firstStop.place)}${firstStop.tagline ? `<em>${escapeHtml(firstStop.tagline)}</em>` : ''}</h2>
        ${first.subtitle ? `<p class="lx-g-sub">${escapeHtml(first.subtitle)}</p>` : ''}
        ${audioSrc ? player({ url: audioSrc, label: 'Listen to this stop', aria: `Play stop 1: ${firstStop.place}` }, 'lx-play-sm') : ''}
        <div class="lx-g-body">${firstBody}</div>
      </div>
      ${first.photoUrl ? `<figure class="lx-g-photo"><img src="${escapeHtml(first.photoUrl)}" alt="${escapeHtml(firstStop.place)}" loading="lazy" decoding="async"></figure>` : ''}
    </div>
  </section>` : '';

  const routeSection = N > 1 ? `
  <section class="lx-sec lx-sec-paper" id="route">
    <div class="lx-g-route">
      <div class="lx-head"><p class="lx-kicker">The route</p><h2>${N} stops,<em> one story</em></h2>
      ${about.slice(0, 2).map(p => `<p>${escapeHtml(p)}</p>`).join('')}</div>
      <ol class="lx-stops">
        ${stops.map(s => `<li${s.n === 1 ? ' class="is-open"' : ''}><span class="lx-stop-n">${s.n}</span><div><h3>${escapeHtml(s.place)}</h3>${s.tagline ? `<p class="lx-stop-tag">${escapeHtml(s.tagline)}</p>` : ''}${s.teaser ? `<p>${escapeHtml(s.teaser)}</p>` : ''}${s.n === 1 ? '<a class="lx-stop-link" href="#first-stop">Read it above ↑</a>' : ''}</div></li>`).join('\n        ')}
      </ol>
      <div class="lx-unlock">
        <div><h3>Hear all ${N} stops in the app</h3>
        <p>The app plays every stop, shows where you are on the map and answers your questions on the way. Free to start your first tour.</p></div>
        ${stores('route', true, 'guide')}
        ${maps ? `<a class="lx-maps" href="${escapeHtml(maps.url)}" target="_blank" rel="noopener" data-gs-event="guide_maps_click">${escapeHtml(maps.label)} →</a>` : ''}
      </div>
    </div>
  </section>` : '';

  const relatedCards = related.map(g => `<article class="lx-card">`
    + `<a class="lx-card-img" href="${escapeHtml(g.url)}" tabindex="-1" aria-hidden="true">${g.thumbnail ? `<img src="${escapeHtml(g.thumbnail)}" alt="" loading="lazy" decoding="async" width="640" height="400">` : ''}</a>`
    + `<div class="lx-card-body">${g.stops ? `<div class="lx-card-meta"><span>${g.stops} stops</span></div>` : ''}<h3><a href="${escapeHtml(g.url)}">${escapeHtml(tidyTitle(g.name))}</a></h3>`
    + `<div class="lx-card-foot"><span></span><a class="lx-open" href="${escapeHtml(g.url)}">Open guide <span aria-hidden="true">→</span></a></div></div></article>`).join('');
  const moreSection = related.length || cityPage ? `
  <section class="lx-sec" id="more">
    <div class="lx-head"><p class="lx-kicker">Keep exploring</p><h2>More guides in<em> ${escapeHtml(city || 'the area')}</em></h2></div>
    ${related.length ? `<div class="lx-grid">${relatedCards}</div>` : ''}
    ${cityPage ? `<p class="lx-g-city"><a class="lx-btn" href="${escapeHtml(cityPage.url)}">See all ${escapeHtml(city)} audio guides →</a></p>` : ''}
  </section>` : '';

  return `<!DOCTYPE html>
<html lang="${escapeHtml(lang)}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(title)}</title>
  <meta name="description" content="${escapeHtml(description)}">
  <link rel="canonical" href="${escapeHtml(url)}">
  <meta name="robots" content="index, follow, max-image-preview:large">
  <meta property="og:type" content="website">
  <meta property="og:site_name" content="GuideSofia">
  <meta property="og:url" content="${escapeHtml(url)}">
  <meta property="og:title" content="${escapeHtml(title)}">
  <meta property="og:description" content="${escapeHtml(description)}">
  ${image ? `<meta property="og:image" content="${escapeHtml(image)}">` : ''}
  <meta name="twitter:card" content="summary_large_image">
  <meta name="apple-itunes-app" content="app-id=6748287909">
${ldHtml}
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css">
  <link rel="stylesheet" href="../../seo-styles.css">
  <link rel="stylesheet" href="../../city-page.css">
  <script async src="https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TAG}"></script>
  <script>window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GOOGLE_TAG}');</script>
</head>
<body>
<div id="header-container"></div>
<main class="lx lx-guide" id="main" data-page="guide">
  <header class="lx-hero lx-hero-guide">
    ${image ? `<img class="lx-hero-bg" src="${escapeHtml(image)}" alt="" fetchpriority="high">` : ''}
    <div class="lx-hero-in">
      <nav class="lx-crumbs" aria-label="Breadcrumb">${crumbHtml}</nav>
      <p class="lx-eyebrow">${escapeHtml([city, country].filter(Boolean).join(' · '))}${city || country ? ' · ' : ''}Self-guided audio tour</p>
      <h1>${escapeHtml(name)}</h1>
      ${intro ? `<p class="lx-lead">${escapeHtml(intro)}</p>` : ''}
      <div class="lx-hero-actions">${heroPlayer}${stores('hero', false, 'guide')}</div>
      <dl class="lx-stats">
        <div><dt>Stops</dt><dd>${N}</dd></div>
        <div><dt>App languages</dt><dd>30+</dd></div>
        <div><dt>Offline</dt><dd>Yes</dd></div>
        <div><dt>Updated</dt><dd class="lx-g-date">${escapeHtml(updated)}</dd></div>
      </dl>
    </div>
  </header>
${firstSection}${routeSection}${moreSection}

  <section class="lx-sec lx-sec-paper" id="faq">
    <div class="lx-head"><p class="lx-kicker">FAQ</p><h2>Questions about<em> this guide</em></h2></div>
    <div class="lx-faq">${faq}</div>
  </section>

  <section class="lx-cta">
    <div class="lx-cta-in">
      <h2>Take ${escapeHtml(name)}<em> in your pocket</em></h2>
      <p>Download GuideSofia, open this guide and start walking. It's free to start your first tour.</p>
      ${stores('cta', true, 'guide')}
    </div>
  </section>

  <aside class="lx-b2b">
    <div><p class="lx-kicker">For museums &amp; cities</p>
    <h2>Run a museum or heritage site${city ? ` in ${escapeHtml(city)}` : ''}?</h2>
    <p>We turn the texts you already have into an audio guide on the visitor's own phone, approved by your team.</p></div>
    <div class="lx-b2b-links"><a class="lx-btn" href="https://www.guidesofia.com/business/#demo" data-gs-event="demo_click_guide">Book a 15-minute demo</a><a class="lx-btn-ghost" href="https://www.guidesofia.com/business/">How it works</a></div>
  </aside>

  <div class="lx-bar" aria-hidden="true"><span>${escapeHtml(shorten(name, 34))}${N > 1 ? ` · ${N} stops` : ''}</span><a href="https://apps.apple.com/app/6748287909" data-store>Get the app</a></div>
</main>
<audio id="lx-audio" preload="none"></audio>
<div id="footer-container"></div>
<script src="../../city-page.js"></script>
<script src="../../seo-scripts.js"></script>
</body>
</html>
`;
}

/**
 * Creates a URL-friendly slug from a string, removing accents
 */
function createSlug(name) {
  if (!name) return 'guide';
  return sanitizeFilename(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/**
 * Sanitizes filenames by removing accents and special characters
 * ã → a, ç → c, é → e, etc.
 */
function sanitizeFilename(name) {
  if (!name) return '';
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .replace(/[^\w\s.-]/g, ''); // Remove special characters
}

/**
 * Extract unique media files from guide
 */
function extractMediaFiles(guide) {
  const mediaFiles = { images: [], audio: [] };
  const seen = new Set();

  const points = guide.points || [];

  points.forEach((stop) => {
    // Collect images
    if (stop.photoUrl && !seen.has(stop.photoUrl)) {
      mediaFiles.images.push(stop.photoUrl);
      seen.add(stop.photoUrl);
    }
    // Collect audio
    if (stop.audioUrl && !seen.has(stop.audioUrl)) {
      mediaFiles.audio.push(stop.audioUrl);
      seen.add(stop.audioUrl);
    }
  });

  return mediaFiles;
}

/**
 * Generate local filename from URL
 */
function getLocalFilename(url, index, type) {
  try {
    const urlObj = new URL(url);
    const ext = urlObj.pathname.split('.').pop() || type;
    return `${type}-${index + 1}.${ext}`;
  } catch {
    return `${type}-${index + 1}.${type === 'audio' ? 'mp3' : 'jpg'}`;
  }
}

/**
 * Replace media URLs in HTML with local filenames
 */
function replaceMediaUrlsInHtml(html, mediaMap) {
  let updatedHtml = html;

  // Replace image URLs
  mediaMap.images.forEach(({ original, local }) => {
    // Escape special regex characters in URL
    const escapedUrl = original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedUrl, 'g');
    updatedHtml = updatedHtml.replace(regex, `./${local}`);
  });

  // Replace audio URLs
  mediaMap.audio.forEach(({ original, local }) => {
    const escapedUrl = original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedUrl, 'g');
    updatedHtml = updatedHtml.replace(regex, `./${local}`);
  });

  return updatedHtml;
}

/**
 * Download file from URL as blob
 */
async function downloadFile(url) {
  try {
    // Try with CORS first
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      // Try without CORS (no-cors mode doesn't work for blobs, but this helps diagnose)
      throw new Error(`HTTP ${response.status}`);
    }
    return await response.blob();
  } catch (error) {
    console.warn(`Failed to download from ${url}:`, error.message);
    return null;
  }
}

/**
 * Escapes HTML special characters to prevent XSS
 */
function escapeHtml(text) {
  if (!text) return '';
  const str = String(text);
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Get language display name from code
 */
function getLanguageName(code) {
  const languages = {
    'en': 'English',
    'pt': 'Portuguese',
    'pt-PT': 'Portuguese',
    'pt-BR': 'Portuguese',
    'es': 'Spanish',
    'fr': 'French',
    'de': 'German',
    'it': 'Italian',
    'nl': 'Dutch',
    'zh': 'Chinese',
    'ja': 'Japanese',
    'ko': 'Korean'
  };
  return languages[code] || code || 'Multiple';
}

/**
 * Downloads the generated PHP file with all media in a ZIP
 * Now calls Cloud Function to handle downloads server-side (avoids CORS issues)
 */
export async function downloadSeoPage(guide) {
  const metadata = guide.metadata || {};
  const guideSlug = createSlug(metadata.name || 'guide');

  // Generate base PHP/HTML content
  const htmlContent = generateSeoPage(guide);

  console.log(`[SEO Download] Requesting ZIP package for: ${metadata.name}`);
  console.log('[DEBUG Frontend] Guide data:', guide);
  console.log('[DEBUG Frontend] Total points:', guide.points?.length);
  console.log('[DEBUG Frontend] First stop:', guide.points?.[0]);
  console.log('[DEBUG Frontend] First stop audioUrl:', guide.points?.[0]?.audioUrl);

  try {
    // Call Cloud Function to generate ZIP with media
    const response = await fetch(
      'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendItineraryBuilder_generateSeoZip',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          guide: guide,
          htmlContent: htmlContent
        })
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.details || error.error || `HTTP ${response.status}`);
    }

    // Get ZIP file blob
    const zipBlob = await response.blob();

    // Download ZIP file
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${guideSlug}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`[SEO Download] Package downloaded: ${guideSlug}.zip`);
    return `${guideSlug}.zip`;

  } catch (error) {
    console.error('[SEO Download] Error:', error);
    throw new Error(`Failed to generate SEO package: ${error.message}`);
  }
}

export default { generateSeoPage, downloadSeoPage };
